import { useEffect } from 'react'
import type { IntlShape } from 'react-intl'
import type { NotificationInstance } from 'antd/es/notification/interface'

import { markOnboardingCallCtaBooked } from './onboarding-call-cta-booking-storage'

const CALENDLY_ORIGIN = 'https://calendly.com'
const EVENT_SCHEDULED = 'calendly.event_scheduled'

function isCalendlyEventScheduledMessage(event: MessageEvent): boolean {
  if (event.origin !== CALENDLY_ORIGIN) return false
  const { data } = event
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as { event?: string }
      return parsed?.event === EVENT_SCHEDULED
    } catch {
      return false
    }
  }
  if (typeof data === 'object' && data !== null && 'event' in data) {
    return (data as { event: string }).event === EVENT_SCHEDULED
  }
  return false
}

export interface UseCalendlyBookingPostMessageListenerArgs {
  open: boolean
  userId: string
  companyId: string
  onClose: () => void
  formatMessage: IntlShape['formatMessage']
  notification: Pick<NotificationInstance, 'success'>
}

/**
 * Subscribes to Calendly iframe `postMessage` when the embed modal is open.
 * Lives in a hook so the modal component stays free of `useEffect` (browser integration).
 */
export function useCalendlyBookingPostMessageListener({ open, userId, companyId, onClose, formatMessage, notification }: UseCalendlyBookingPostMessageListenerArgs): void {
  useEffect(() => {
    if (!open) return
    const handler = (e: MessageEvent) => {
      if (!isCalendlyEventScheduledMessage(e)) return
      markOnboardingCallCtaBooked(userId, companyId)
      notification.success({
        message: formatMessage({ id: 'onboarding.callCta.bookedNotification.title' }),
        description: formatMessage({ id: 'onboarding.callCta.bookedNotification.description' }),
        duration: 6,
        placement: 'topRight',
      })
      onClose()
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [open, userId, companyId, onClose, formatMessage, notification])
}
